import { formatDecimal, formatEuro, formatShortDate } from "@shared";
import { relationshipLabelKey } from "@shared/client-file/asset-inventory/utils/relationshipLabels.utils";
import {
  changeHistoryEntityLabels,
  changeHistoryFieldLabels,
  changeHistoryFixedSetValueLabels,
} from "@shared/i18n/de/changeHistory";

import {
  ChangeEntryKind,
  ClientChangeHistoryEntryResponse,
  EntitySubtypeResponse,
  FieldChangeResponse,
  FieldValueType,
  RelationshipType,
} from "@/api";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import formatTimestamp from "@/common/utils/formatTimestamp";
import { formatUserCode } from "@/common/utils/formatUserCode";
import { ITranslation, LanguageType } from "@/translations";

/**
 * The changed field's German label. A field of a classified entry can be named per type — a contact
 * detail's value is an "E-Mail" or a "Nummer" — so the entry's type is tried first.
 */
function getFieldLabel(fieldKey: string, subtype: EntitySubtypeResponse | null): string {
  const perTypeLabel = subtype == null ? undefined : changeHistoryFieldLabels[`${fieldKey}.${subtype.value}`];

  return perTypeLabel ?? changeHistoryFieldLabels[fieldKey] ?? fieldKey;
}

function getEntityLabel(entityType: string): string {
  return changeHistoryEntityLabels[entityType] ?? entityType;
}

/**
 * Formats one side (old/new) of a field change for display. Server-resolved display values
 * (references) win; otherwise the raw invariant value is formatted by its type hint. Unknown values
 * degrade to the raw string, and an empty side is null — the columns render the placeholder for it,
 * so a side that held nothing is not struck through as if it had.
 */
function formatChangeValue(
  field: FieldChangeResponse,
  side: "old" | "new",
  t: ITranslation,
  language: LanguageType
): string | null {
  const displayValue = side === "old" ? field.oldDisplayValue : field.newDisplayValue;
  if (displayValue) {
    return displayValue;
  }

  const rawValue = side === "old" ? field.oldValue : field.newValue;
  if (rawValue == null || rawValue === "") {
    return null;
  }

  switch (field.valueType) {
    case FieldValueType.Money:
      return formatEuro(Number(rawValue), language);
    case FieldValueType.Percent:
      return `${formatDecimal(Number(rawValue), language)} %`;
    case FieldValueType.Date:
      return formatShortDate(new Date(rawValue), locales[language]);
    case FieldValueType.DateTime:
      return formatTimestamp(new Date(rawValue), t, language);
    case FieldValueType.Time:
      return `${rawValue} ${t("common.clock")}`;
    case FieldValueType.Boolean:
      return rawValue === "true" ? t("common.yes") : t("common.no");
    case FieldValueType.FixedSet:
      return formatFixedSetValue(field.fieldKey, rawValue, t);
    default:
      return rawValue;
  }
}

function formatFixedSetValue(fieldKey: string, rawValue: string, t: ITranslation): string {
  const mappedLabel = changeHistoryFixedSetValueLabels[`${fieldKey}.${rawValue}`];
  if (mappedLabel) {
    return mappedLabel;
  }

  if (fieldKey === "assetDependant.relationship" && isRelationshipType(rawValue)) {
    return t(relationshipLabelKey(rawValue));
  }

  return rawValue;
}

function isRelationshipType(value: string): value is RelationshipType {
  return (Object.values(RelationshipType) as string[]).includes(value);
}

/** One table row of the change-history grid: a single field change or a created/added/removed entry. */
export interface ChangeHistoryRow {
  id: string;
  changedAtUtc: Date;
  kindLabel: string;
  /**
   * The entry the row belongs to — its name ("Aktübergabe 13.08.2026"), its type ("E-Mail"), or its
   * kind of record where the name was left empty ("Konto"). Null where the row has no entry:
   * one-per-client data, and a history scoped to one entry the page already names.
   */
  entryLabel: string | null;
  /** The changed field's label, or the creation text; null for added/removed entries. */
  field: string | null;
  oldValue: string | null;
  newValue: string | null;
  user: string;
}

/**
 * Flattens the history entries into grid rows — one row per changed field, one row per
 * created/added/removed record. Values are formatted for display here so the grid works on plain
 * strings rather than reformatting per cell render.
 */
export function buildChangeHistoryRows(
  entries: ClientChangeHistoryEntryResponse[],
  t: ITranslation,
  language: LanguageType
): ChangeHistoryRow[] {
  const rows: ChangeHistoryRow[] = [];

  for (const entry of entries) {
    // The backend sets at most one of these three; see ClientChangeHistoryEntryResponse for which
    // and why. A classified entry has no name — its data travels as the entry value instead.
    const classifierLabel =
      entry.entitySubtype == null
        ? null
        : formatFixedSetValue(entry.entitySubtype.fieldKey, entry.entitySubtype.value, t);
    const entryLabel =
      classifierLabel ?? entry.entityName ?? (entry.isIdentifiedByEntityType ? getEntityLabel(entry.entityType) : null);

    const base = {
      changedAtUtc: entry.changedAtUtc,
      user: formatUserCode(entry.userCode, t),
      entryLabel,
    };

    switch (entry.kind) {
      case ChangeEntryKind.Modified:
        for (const fieldChange of entry.fieldChanges) {
          rows.push({
            ...base,
            id: `${rows.length}`,
            kindLabel: t("changeHistory.entry.fieldChanged"),
            field: getFieldLabel(fieldChange.fieldKey, entry.entitySubtype),
            oldValue: formatChangeValue(fieldChange, "old", t, language),
            newValue: formatChangeValue(fieldChange, "new", t, language),
          });
        }
        break;
      case ChangeEntryKind.Added:
      case ChangeEntryKind.Removed:
        rows.push({
          ...base,
          id: `${rows.length}`,
          kindLabel:
            entry.kind === ChangeEntryKind.Added
              ? t("changeHistory.entry.entryAdded")
              : t("changeHistory.entry.entryRemoved"),
          // Announced by WHAT it is, never by its name — that goes in the value column.
          entryLabel: classifierLabel ?? getEntityLabel(entry.entityType),
          field: null,
          oldValue: entry.kind === ChangeEntryKind.Removed ? entry.entityValue : null,
          newValue: entry.kind === ChangeEntryKind.Added ? entry.entityValue : null,
        });
        break;
      case ChangeEntryKind.Created:
        rows.push({
          ...base,
          id: `${rows.length}`,
          kindLabel: t("changeHistory.entry.created"),
          field: t("changeHistory.entry.createdText", { entity: getEntityLabel(entry.entityType) }),
          oldValue: null,
          newValue: null,
        });
        break;
    }
  }

  return rows;
}
